import React, { useMemo, useState, useEffect } from 'react';
import { IioTable } from '../../../components/IioTable';
import { useProject } from '../../../hooks/useProject';
import { useCollectionEventToasts, useResumeCollection } from '../../../hooks/useCollections';
import { hasPermission } from '../../../types/project';
import { openNewCollectionDialog } from '../../../services/angular-bridge';
import { navigateToAngular } from '../../../services/navigate';
import type { Collection } from '../../../types/collection';
import { FilterInput } from '../../../components/FilterInput';
import { HelpLink } from '../../../components/HelpLink';
import { SortableTh } from '../../../components/SortableTh';
import { compareBy, type SortDir } from '../../../utilities/sort';
import { CollectionRow } from './CollectionRow';

type SortKey = 'name' | 'description' | 'custodianName' | 'createdAt';

const STATUS_LABEL: Record<string, string> = {
  processed:    'label-success',
  processing:   'label-danger',
  initializing: 'label-info',
  new:          'label-info',
  syncing:      'label-info',
  paused:       'label-warning',
};

function statusLabel(status: string | null | undefined): string {
  return STATUS_LABEL[status?.toLowerCase() ?? ''] ?? 'label-default';
}

function collectionSortValue(c: Collection, key: SortKey): string | number {
  switch (key) {
    case 'name':          return c.name ?? '';
    case 'description':   return c.description ?? '';
    case 'custodianName': return c.custodian?.name ?? '';
    case 'createdAt':     return c.createdAt ?? 0;
  }
}

function sortCollections(list: Collection[], key: SortKey, dir: SortDir): Collection[] {
  return [...list].sort(compareBy(c => collectionSortValue(c, key), dir));
}

function collectionMatchesFilter(c: Collection, q: string): boolean {
  const lq = q.toLowerCase();
  return c.name.toLowerCase().includes(lq) ||
    (c.description ?? '').toLowerCase().includes(lq) ||
    (c.custodian?.name ?? '').toLowerCase().includes(lq);
}

interface Props {
  projectId: string;
  externalFilter?: string;
}

export function CollectionsTab({ projectId, externalFilter }: Props) {
  const { data: project } = useProject(projectId);
  const canManageCollections = project ? hasPermission(project, 'MANAGE_COLLECTIONS') : false;
  useCollectionEventToasts(projectId);
  const resumeCollection = useResumeCollection(projectId);

  const [nameFilter, setNameFilter] = useState(externalFilter ?? '');

  useEffect(() => {
    if (externalFilter !== undefined) setNameFilter(externalFilter);
  }, [externalFilter]);
  const [selectOpen, setSelectOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [resumingIds, setResumingIds] = useState<Set<string>>(new Set());
  const [procSortKey, setProcSortKey] = useState<SortKey>('createdAt');
  const [procSortDir, setProcSortDir] = useState<SortDir>('desc');
  const [doneSort, setDoneSort] = useState<SortKey>('createdAt');
  const [doneSortDir, setDoneSortDir] = useState<SortDir>('desc');

  const allCollections: Collection[] = project?.collections ?? [];

  const processingCollections = useMemo(() =>
    allCollections.filter(c => c.status?.toLowerCase() !== 'processed'),
    [allCollections]);

  const processedCollections = useMemo(() =>
    allCollections.filter(c => c.status?.toLowerCase() === 'processed'),
    [allCollections]);

  const filteredProcessing = useMemo(() =>
    sortCollections(
      processingCollections.filter(c => collectionMatchesFilter(c, nameFilter)),
      procSortKey, procSortDir,
    ), [processingCollections, nameFilter, procSortKey, procSortDir]);

  const filteredProcessed = useMemo(() =>
    sortCollections(
      processedCollections.filter(c => collectionMatchesFilter(c, nameFilter)),
      doneSort, doneSortDir,
    ), [processedCollections, nameFilter, doneSort, doneSortDir]);

  const numChecked = checkedIds.size;

  function toggleCheck(id: string) {
    setCheckedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function checkAll() {
    setCheckedIds(new Set(allCollections.map(c => c.id)));
  }

  function checkNone() {
    setCheckedIds(new Set());
  }

  function resumeProcessing(collectionId: string) {
    setResumingIds(prev => new Set([...prev, collectionId]));
    resumeCollection.mutate(collectionId, {
      onSettled: () => setResumingIds(prev => { const next = new Set(prev); next.delete(collectionId); return next; }),
    });
  }

  function handleProcSort(key: SortKey) {
    if (key === procSortKey) setProcSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setProcSortKey(key); setProcSortDir('asc'); }
  }

  function handleDoneSort(key: SortKey) {
    if (key === doneSort) setDoneSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setDoneSort(key); setDoneSortDir('asc'); }
  }

  const allNames = allCollections.map(c => c.name);

  const renderRow = (c: Collection, variant: 'processing' | 'processed') => {
    const isPausedNonTrial = c.status === 'Paused' && project?.status !== 'trial';
    return (
      <CollectionRow
        key={c.id}
        collection={c}
        projectId={projectId}
        variant={variant}
        checked={checkedIds.has(c.id)}
        onToggleCheck={toggleCheck}
        statusClass={statusLabel(c.status)}
        showResume={variant === 'processing' && isPausedNonTrial}
        isResuming={resumingIds.has(c.id)}
        onResume={() => resumeProcessing(c.id)}
        canRename={canManageCollections}
        allNames={allNames}
        onCustodianClick={name => setNameFilter(name)}
      />
    );
  };

  const renderSortableHeaders = (current: SortKey, dir: SortDir, onSort: (k: SortKey) => void) => (
    <>
      <SortableTh label="Name" sortKey="name" current={current} dir={dir} onSort={onSort} />
      <SortableTh label="Description" sortKey="description" current={current} dir={dir} onSort={onSort} />
      <SortableTh label="Custodian" sortKey="custodianName" current={current} dir={dir} onSort={onSort} />
      <th>Source</th>
      <th>Output</th>
      <SortableTh label="Created" sortKey="createdAt" current={current} dir={dir} onSort={onSort} />
    </>
  );

  return (
    <>
      <h2>
        <i className="far fa-hdd" aria-hidden="true" />{' '}
        {project?.name} Collections
      </h2>

      <div className="row">
        <div className="col-md-8">
          <div className="btn-toolbar">
            <div className={`btn-group${selectOpen ? ' open' : ''}`}>
              <button className="btn btn-default" onClick={() => setSelectOpen(v => !v)}>
                <span className="far fa-check-square fa-fw" aria-hidden="true" />
                {' '}<span className="caret" />
              </button>
              {selectOpen && (
                <ul className="dropdown-menu">
                  <li><a onClick={() => { checkAll(); setSelectOpen(false); }}>All</a></li>
                  <li><a onClick={() => { checkNone(); setSelectOpen(false); }}>None</a></li>
                </ul>
              )}
            </div>

            <div className={`btn-group${actionsOpen ? ' open' : ''}`}>
              <button
                className="btn btn-default"
                onClick={() => setActionsOpen(v => !v)}
                disabled={numChecked === 0}
              >
                Actions <span className="caret" />
              </button>
              {actionsOpen && (
                <ul className="dropdown-menu">
                  <li>
                    <a onClick={() => { navigateToAngular(`/project/${projectId}/browse`); setActionsOpen(false); }}>
                      Explore Tags
                    </a>
                  </li>
                </ul>
              )}
            </div>

            {canManageCollections && (
              <>
                <button
                  className="btn btn-primary"
                  onClick={() => project && openNewCollectionDialog(project)}
                >
                  Add Collection <i className="fas fa-plus-circle" aria-hidden="true" />
                </button>
                {' '}
                <HelpLink href="https://indexedio.zendesk.com/hc/en-us/articles/213127166-Adding-Data-To-Your-Project" />
              </>
            )}

            <FilterInput value={nameFilter} onChange={setNameFilter} />
          </div>
        </div>
        <div className="col-md-4" style={{ textAlign: 'right' }}>
          <p className="lead">You have {allCollections.length} Collections</p>
        </div>
      </div>

      {filteredProcessing.length > 0 && (
        <>
          <h4>Processing Collections</h4>
          <IioTable condensed>
            <thead>
              <tr>
                <th />
                {renderSortableHeaders(procSortKey, procSortDir, handleProcSort)}
                <th>Status</th>
                <th>Progress</th>
              </tr>
            </thead>
            <tbody>
              {filteredProcessing.map(c => renderRow(c, 'processing'))}
            </tbody>
          </IioTable>
        </>
      )}

      <h4>Processed Collections</h4>
      <IioTable condensed>
        <thead>
          <tr>
            <th />
            {renderSortableHeaders(doneSort, doneSortDir, handleDoneSort)}
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {filteredProcessed.map(c => renderRow(c, 'processed'))}
        </tbody>
      </IioTable>
    </>
  );
}
